import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { AnchorOrgRole } from "lib/types/graphQLEnums";
import { CompetitorPartnerCountOnBuyerQuery } from "../../../generated/app-service-gql/graphql";
import { useSavedSuppliers } from "../signals/useSavedSuppliers";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const query = graphql(`
  query competitorPartnerCountOnBuyer($req: NoticeCountByCrossRefOrgRequest!) {
    noticeCountByCrossRefOrg(NoticeCountByCrossRefOrgRequest: $req) {
      orgId
      noticeCount
    }
  }
`);

export function useCompetitorPartnerCountOnBuyer(
  buyerId: string,
  options?: UseQueryOptions<
    CompetitorPartnerCountOnBuyerQuery,
    unknown,
    CompetitorPartnerCountOnBuyerQuery,
    QueryKey
  >,
) {
  const { data: savedSuppliers, isLoading: isLoadingSavedSuppliers } = useSavedSuppliers({
    enabled: options?.enabled,
  });

  const isLoadingSuppliers = options?.enabled && isLoadingSavedSuppliers;

  const compGuids = savedSuppliers?.competitorGuids ?? [];
  const partnerGuids = savedSuppliers?.partnerGuids ?? [];

  // Sorting just in case. Would like to keep query cache consistent if saved suppliers don't change
  const savedSupplierGuids = compGuids.concat(partnerGuids).sort();

  const isEnabled = options?.enabled && savedSupplierGuids.length > 0;

  // Ensure useGraphQlQuery is always called, but prevent fetching if no suppliers exist
  const { data, isLoading, isFetching, ...rest } = useGraphQlQuery(
    ["competitorPartnerCountOnBuyer", buyerId, savedSupplierGuids],
    query,
    [
      {
        req: {
          anchorOrgId: buyerId,
          anchorRole: AnchorOrgRole.Buyer,
          targetOrgIds: savedSupplierGuids,
        },
      },
    ],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
      enabled: isEnabled, // Prevent fetching if no saved suppliers or disabled
    },
  );

  const noPartners = partnerGuids.length === 0 || !data?.noticeCountByCrossRefOrg;

  const noCompetitors = compGuids.length === 0 || !data?.noticeCountByCrossRefOrg;

  // If disabled, return default values but keep hook order intact
  const partnerCount = noPartners
    ? 0
    : data.noticeCountByCrossRefOrg.filter((org) => partnerGuids.includes(org.orgId)).length;

  const competitorCount = noCompetitors
    ? 0
    : data.noticeCountByCrossRefOrg.filter((org) => compGuids.includes(org.orgId)).length;

  return {
    partnerCount,
    competitorCount,
    isLoading: isLoadingSuppliers || (savedSupplierGuids.length > 0 && isLoading),
    isFetching: savedSupplierGuids.length > 0 && isFetching,
    ...rest,
  };
}
